import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import clientPortalService from '../../services/clientPortalService';
import PropertyPortfolioCard from './components/PropertyPortfolioCard';
import TransactionStatusCard from './components/TransactionStatusCard';
import ContractDeadlineCard from './components/ContractDeadlineCard';
import PaymentScheduleTable from './components/PaymentScheduleTable';
import DocumentLibraryPanel from './components/DocumentLibraryPanel';
import CommunicationCenterPanel from './components/CommunicationCenterPanel';
import DashboardMetrics from './components/DashboardMetrics';

const ClientPropertyAndStatusDashboard = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientProfile, setClientProfile] = useState(null);
  const [properties, setProperties] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get client profile first
      const profile = await clientPortalService?.getClientProfile(user?.id);
      setClientProfile(profile);

      if (!profile?.id) {
        throw new Error('لم يتم العثور على ملف العميل');
      }

      // Load all dashboard data in parallel
      const [
        propertiesData,
        transactionsData,
        deadlinesData,
        paymentsData,
        documentsData,
        communicationsData,
        appointmentsData
      ] = await Promise.all([
        clientPortalService?.getClientProperties(profile?.id),
        clientPortalService?.getPendingTransactions(profile?.id),
        clientPortalService?.getContractDeadlines(profile?.id),
        clientPortalService?.getPaymentSchedule(profile?.id),
        clientPortalService?.getClientDocuments(profile?.id),
        clientPortalService?.getCommunicationHistory(profile?.id),
        clientPortalService?.getUpcomingAppointments(profile?.id)
      ]);

      setProperties(propertiesData);
      setTransactions(transactionsData);
      setDeadlines(deadlinesData);
      setPayments(paymentsData);
      setDocuments(documentsData);
      setCommunications(communicationsData);
      setAppointments(appointmentsData);

      // Calculate metrics
      setMetrics({
        totalProperties: propertiesData?.length || 0,
        activeContracts: propertiesData?.filter(p => p?.contractStatus === 'active')?.length || 0,
        pendingTransactions: transactionsData?.filter(t => t?.status === 'pending')?.length || 0,
        pendingSignatures: 0 // Would come from signature requests
      });

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'فشل في تحميل بيانات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })?.format(date);
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                لوحة تحكم العميل
              </h1>
              <p className="text-muted-foreground">
                مرحباً {clientProfile?.name || userProfile?.full_name || 'عزيزي العميل'}
              </p>
            </div>
            <button
              onClick={loadDashboardData}
              className="p-3 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
              title="تحديث البيانات"
            >
              <Icon name="RefreshCw" size={20} color="var(--color-primary)" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <p className="text-error">{error}</p>
            <Button variant="outline" size="sm" onClick={loadDashboardData}>
              إعادة المحاولة
            </Button>
          </div>
        )}

        {/* Dashboard Metrics */}
        <DashboardMetrics metrics={metrics} loading={loading} />

        {/* Property Portfolio Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={24} color="var(--color-primary)" />
              <h2 className="text-xl font-bold text-foreground">محفظة العقارات</h2>
            </div>
            <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-semibold">
              {properties?.length || 0} عقار
            </span>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="bg-card rounded-xl border border-border p-4 animate-pulse">
                  <div className="h-48 bg-muted rounded-lg mb-4"></div>
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : properties?.length === 0 ? (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <Icon name="Building2" size={48} color="var(--color-muted-foreground)" />
              <p className="text-muted-foreground mt-3">لا توجد عقارات مسجلة</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties?.map((property, index) => (
                <PropertyPortfolioCard 
                  key={index} 
                  property={property?.property}
                  onClick={() => console.log('Property clicked:', property)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Transactions */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Clock" size={24} color="var(--color-warning)" />
              <h2 className="text-xl font-bold text-foreground">المعاملات المعلقة</h2>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2]?.map(i => (
                    <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              ) : transactions?.length === 0 ? (
                <div className="bg-card rounded-lg border border-border p-6 text-center">
                  <Icon name="CheckCircle" size={40} color="var(--color-success)" />
                  <p className="text-muted-foreground mt-3">لا توجد معاملات معلقة</p>
                </div>
              ) : (
                transactions?.slice(0, 3)?.map((transaction, index) => (
                  <TransactionStatusCard key={index} transaction={transaction} />
                ))
              )}
            </div>
          </section>

          {/* Contract Deadlines */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Calendar" size={24} color="var(--color-error)" />
              <h2 className="text-xl font-bold text-foreground">مواعيد العقود</h2>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2]?.map(i => (
                    <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
                      <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              ) : deadlines?.length === 0 ? (
                <div className="bg-card rounded-lg border border-border p-6 text-center">
                  <Icon name="Calendar" size={40} color="var(--color-muted-foreground)" />
                  <p className="text-muted-foreground mt-3">لا توجد مواعيد قادمة</p>
                </div>
              ) : (
                deadlines?.slice(0, 3)?.map((deadline, index) => (
                  <ContractDeadlineCard key={index} deadline={deadline} />
                ))
              )}
            </div>
          </section>
        </div>

        {/* Payment Schedule */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Receipt" size={24} color="var(--color-primary)" />
            <h2 className="text-xl font-bold text-foreground">جدول الدفعات</h2>
          </div>
          <PaymentScheduleTable payments={payments} loading={loading} />
        </section>

        {/* Two Column Layout - Documents & Communications */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Document Library */}
          <section>
            <DocumentLibraryPanel documents={documents} loading={loading} />
          </section>

          {/* Communication Center */}
          <section>
            <CommunicationCenterPanel communications={communications} loading={loading} />
          </section>
        </div>

        {/* Upcoming Appointments */}
        {appointments?.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Calendar" size={24} color="var(--color-primary)" />
              <h2 className="text-xl font-bold text-foreground">المواعيد القادمة</h2>
            </div>
            <div className="bg-card rounded-lg border border-border p-4">
              <div className="space-y-3">
                {appointments?.map((apt, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Calendar" size={18} color="var(--color-primary)" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground text-sm">{apt?.title}</h4>
                      <p className="text-xs text-muted-foreground">{formatDate(apt?.appointmentDate)}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{apt?.location}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ClientPropertyAndStatusDashboard;